<template>
    <form>
        <div class="user-contact__title">
            {{ t('title') }}
        </div>
        <div
            v-for="field in props.requiredContactDataFields"
            :key="field.value"
            class="user-contact"
        >
            <input
                v-model.trim="localForm[field.value]"
                class="user-contact__input"
                :placeholder="t(`form.${camelCase(field.value)}`)"
            />
        </div>

        <div v-if="formattedErrors.length > 0" class="error-message-container">
            <div v-show="formattedErrors" class="error-message">
                {{ formattedErrors }}
            </div>
        </div>
    </form>
</template>

<script setup lang="ts">
    import { ContactDataField } from '@keyvue/one-api-client';
    import { computed, ref, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import messages from './UserContact.messages.json';
    import Chat from '@/chat/Chat';
    import type ContactData from '@/chat/types/ContactData';
    import { object, string } from 'yup';

    export interface Props {
        requiredContactDataFields: ContactDataField[],
    }

    const props = defineProps<Props>();

    const chat = Chat.getInstance();
    const { t } = useI18n({ messages });

    const localForm = ref<ContactData>({
        name: null,
        email: null,
        phoneNumber: null,
        postalCode: null,
        houseNumber: null,
    });

    function camelCase(str: string) {
        return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    }

    const isFormEmpty = computed(() => (
        Object.values(localForm.value).join('') === ''
    ));

    const isFormValid = computed(() => (
        !isFormEmpty.value && validationErrors.value.length === 0
    ));

    const validationErrors = computed(() => {
        if (isFormEmpty.value) {
            return [];
        }

        const errors: string[] = [];
        const formValues = localForm.value;

        try {
            validationSchema.value.validateSync(formValues, { abortEarly: false });
        } catch (validationError: any) {
            validationError.inner.forEach((error: any) => {
                errors.push(error.message);
            });
        }

        return errors.length > 0 ? errors : [];
    });

    const formattedErrors = computed(() => {
        const errorMessages = Object.values(validationErrors.value);
        if (errorMessages.length === 0) {
            return '';
        }

        if (errorMessages.length === 1) {
            return t('validationError', { field: errorMessages[0] })
                .toLowerCase()
                .replace(/^[a-z]/, (letter) => letter.toUpperCase());
        }

        return `${errorMessages.slice(0, -1).join(', ')} ${t('validationErrors', { field: errorMessages[errorMessages.length - 1] })}`
            .toLowerCase()
            .replace(/^[a-z]/, (letter) => letter.toUpperCase());
    });

    /** reactive schema since we have configurable fields */
    const validationSchema = computed(() => {
        const fieldValidation = {
            name: string().nullable(),
            email: string().nullable(),
            phoneNumber: string().nullable(),
            postalCode: string().nullable(),
            houseNumber: string().nullable(),
        };

        props.requiredContactDataFields.forEach((field) => {
            switch (field.value) {
                case ContactDataField.NAME.value:
                    fieldValidation.name = string().required();
                    break;
                case ContactDataField.EMAIL.value:
                    fieldValidation.email = string().email(t('form.email')).required();
                    break;
                case ContactDataField.PHONE_NUMBER.value:
                    fieldValidation.phoneNumber = string().matches(/^(?:\+31|0)[1-9]\d{8}$/, t('form.phoneNumber')).required();
                    break;
                case ContactDataField.POSTAL_CODE.value:
                    fieldValidation.postalCode = string().matches(/^[1-9]\d{3}\s?[A-Za-z]{2}$/, t('form.postalCode')).required();
                    break;
                case ContactDataField.HOUSE_NUMBER.value:
                    fieldValidation.houseNumber = string().matches(/^\d+[A-Za-z]?$/, t('form.houseNumber')).required();
                    break;
                default:
            }
        });

        return object().shape(fieldValidation).required();
    });

    watch(localForm, () => {
        chat.changeContactData(localForm.value);
    }, { deep: true });

    defineExpose({
        isFormValid,
    });
</script>

<style scoped lang="scss">
    .user-contact {
        width: 100%;
        padding: 8px 0;

        &__title {
            color: var(--color-widget);
            font-weight: bold;
            font-size: 1rem;
            width: 100%;
        }

        &__input {
            width: 100%;
            height: 30px;
            padding: 5px;

            border-radius: 5px;
            border: 2px solid lightgrey;
        }
    }

    .error-message-container {
        min-height: 1.2rem;
    }

    .error-message {
        color: red;
        font-size: 0.875rem;
    }
</style>
